import { useEffect, useRef, useState } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

/**
 * Mappa degli archi QGIS per la validazione delle geometrie delle tratte.
 *
 * Tutti gli archi del file (~3000) si disegnano una volta sola, su canvas (con SVG
 * sarebbero troppi nodi), e poi si ricolorano a ogni cambio di selezione con setStyle:
 *  - nessuna tratta scelta: colore per stato della tratta dell'arco, rosso tratteggiato
 *    gli archi senza tratta;
 *  - tratta scelta: i suoi archi in blu spesso con i pallini di inizio (verde) e fine
 *    (rosso); in modifica, gli archi degli stessi giorni restano ben visibili e
 *    cliccabili, il resto si spegne; gli archi tolti e non ancora salvati hanno
 *    l'interno grigio e il contorno blu.
 * Pallini bianchi sui giunti fra un arco e l'altro, per distinguere i singoli archi.
 * Leaflet usato direttamente, come in MappaTratte.
 */

export const COLORI = {
    ok: "#16A34A",
    da_verificare: "#F59E0B",
    non_abbinata: "#94A3B8",
    confermata: "#2563EB",
    libero: "#DC2626",
    scelto: "#1D4ED8",
    candidato: "#64748B",
    spento: "#CBD5E1",
    tolto: "#CBD5E1",      // interno dell'arco tolto in modifica (il contorno e' COLORI.scelto)
};
const CENTRO = [44.4949, 11.3426]; // Bologna
// Senza tratta scelta i giunti si mostrano solo da vicino: a zoom piu' basso si affollano.
const GIUNTI_ZOOM_MIN = 16;

// Via e tratto arrivano da file e Interacta e finiscono in HTML: si fanno escape.
const esc = (s) => String(s ?? "").replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));

/* Tooltip di un arco: codice del file QGIS (CODARCO; alcuni archi non ce l'hanno),
   metri, via, tratta con il suo tratto e i giorni dell'arco. */
const testoTooltip = (a, tratta) => [
    `<strong>${a.codarco ? `Arco ${esc(a.codarco)}` : "Arco senza codice"}</strong> · ${Math.round(a.lunghezza)} m`,
    esc(a.street),
    a.tratta ? `Tratta <strong>${esc(a.tratta)}</strong>${tratta?.tratto ? ` · ${esc(tratta.tratto)}` : ""}` : "<em>senza tratta</em>",
    `<span class="gp-geo-tip-giorni">${esc(a.firma.join(", ") || "—")}</span>`,
].join("<br>");

const MappaArchiQgis = ({ archi, tratte, coloreDi, selezione, originali, giorniSel, inizio, fine, modifica, onClickArco, evidenziato, onHoverArco, fitKey, inquadra }) => {
    const nodeRef = useRef(null);
    const mapRef = useRef(null);
    const rendererRef = useRef(null);
    const layersRef = useRef(new Map());     // id arco -> polyline
    const markersRef = useRef(null);
    const aloneRef = useRef(null);           // alone sotto l'arco evidenziato
    const giuntiRef = useRef(null);          // pallini fra un arco e l'altro
    const contorniRef = useRef(null);        // contorno blu degli archi tolti in modifica
    const [vista, setVista] = useState(0);   // cresce a ogni zoom/spostamento: ridisegna i giunti
    /* Centro e zoom al momento in cui la mappa si smonta. Dopo un salvataggio la pagina
       rilegge gli archi e la mappa si ricrea: si riparte da dove si era, non da tutta la citta'. */
    const vistaSalvataRef = useRef(null);
    const clickRef = useRef(onClickArco);
    clickRef.current = onClickArco;
    const hoverRef = useRef(onHoverArco);
    hoverRef.current = onHoverArco;
    // Le tratte (codice -> tratta) si leggono al passaggio del mouse: sempre le ultime.
    const tratteRef = useRef(tratte);
    tratteRef.current = tratte;

    // Mappa e archi: una volta per elenco di archi.
    useEffect(() => {
        if (!nodeRef.current) return undefined;
        /* Un solo canvas per archi e pallini: due renderer canvas nello stesso pane si
           sovrappongono e quello sopra (i pallini) si prende tutti i clic, cosi' gli
           archi sotto non sarebbero piu' cliccabili. */
        const renderer = L.canvas({ tolerance: 10 });   // margine di clic attorno alle linee, in pixel
        const map = L.map(nodeRef.current, { preferCanvas: true, renderer, zoomControl: true }).setView(CENTRO, 13);
        L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
            maxZoom: 19,
            attribution: "&copy; OpenStreetMap",
        }).addTo(map);
        mapRef.current = map;
        rendererRef.current = renderer;
        markersRef.current = L.layerGroup().addTo(map);
        aloneRef.current = L.layerGroup().addTo(map);
        giuntiRef.current = L.layerGroup().addTo(map);
        contorniRef.current = L.layerGroup().addTo(map);
        map.on("moveend", () => setVista((v) => v + 1));
        const layers = new Map();
        const tutti = [];
        for (const a of archi) {
            const pl = L.polyline(a.linea.map(([lng, lat]) => [lat, lng]), { renderer, weight: 3, opacity: 0.8, color: COLORI.spento });
            pl.bindTooltip(() => testoTooltip(a, tratteRef.current?.get(a.tratta)), { sticky: true });
            pl.on("click", () => clickRef.current && clickRef.current(a));
            pl.on("mouseover", () => hoverRef.current && hoverRef.current(a.id));
            pl.on("mouseout", () => hoverRef.current && hoverRef.current(null));
            pl.addTo(map);
            layers.set(a.id, pl);
            tutti.push(pl);
        }
        layersRef.current = layers;
        if (vistaSalvataRef.current) map.setView(vistaSalvataRef.current.centro, vistaSalvataRef.current.zoom);
        else if (tutti.length) map.fitBounds(L.featureGroup(tutti).getBounds(), { padding: [20, 20] });
        const ro = new ResizeObserver(() => map.invalidateSize());
        ro.observe(nodeRef.current);
        setTimeout(() => map.invalidateSize(), 50);
        return () => {
            vistaSalvataRef.current = { centro: map.getCenter(), zoom: map.getZoom() };
            ro.disconnect(); map.remove(); mapRef.current = null; layersRef.current = new Map();
        };
    }, [archi]);

    /* Colori e spessori a ogni cambio di selezione.
       In modifica, un arco della tratta tolto ma non ancora salvato (in `originali`, non
       in `selezione`) ha l'interno grigio e un contorno blu (proposta F scelta da Giacomo):
       il contorno e' una seconda linea, piu' spessa, disegnata sotto l'arco. */
    useEffect(() => {
        const giorni = new Set(giorniSel || []);
        const contorni = contorniRef.current;
        contorni?.clearLayers();
        for (const a of archi) {
            const pl = layersRef.current.get(a.id);
            if (!pl) continue;
            let stile;
            const tolto = modifica && originali?.has(a.id) && !selezione?.has(a.id);
            if (selezione) {
                if (selezione.has(a.id)) stile = { color: COLORI.scelto, weight: 7, opacity: 1, dashArray: null };
                else if (tolto) {
                    stile = { color: COLORI.tolto, weight: 4, opacity: 1, dashArray: null };
                    L.polyline(pl.getLatLngs(), { renderer: rendererRef.current, color: COLORI.scelto, weight: 9, opacity: 1, interactive: false }).addTo(contorni);
                } else if (modifica && a.firma.some((f) => giorni.has(f))) {
                    stile = a.tratta
                        ? { color: COLORI.candidato, weight: 4, opacity: 0.75, dashArray: null }
                        : { color: COLORI.libero, weight: 4, opacity: 0.9, dashArray: "6 5" };
                } else stile = { color: COLORI.spento, weight: 2, opacity: 0.6, dashArray: null };
            } else if (!a.tratta) stile = { color: COLORI.libero, weight: 3, opacity: 0.9, dashArray: "6 5" };
            else stile = { color: coloreDi(a.tratta), weight: 3, opacity: 0.8, dashArray: null };
            pl.setStyle(stile);
            if ((selezione && selezione.has(a.id)) || tolto) pl.bringToFront();   // il tolto sopra al suo contorno
        }
        giuntiRef.current?.eachLayer((m) => m.bringToFront());
    }, [archi, selezione, originali, giorniSel, modifica, coloreDi]);

    /* Giunti fra gli archi (proposta A scelta da Giacomo): un pallino bianco su ogni
       estremo d'arco, cosi' si vede dove finisce un arco e inizia il successivo.
       - tratta scelta (anche in modifica): estremi degli archi selezionati e, in modifica,
         anche di quelli tolti (altrimenti piu' archi tolti vicini sembrano uno solo), bordo blu;
       - nessuna tratta scelta: estremi degli archi con tratta, bordo del colore di stato,
         solo da GIUNTI_ZOOM_MIN in su e solo nell'area visibile (con un po' di margine).
       Due archi contigui condividono l'estremo: un pallino solo (chiave a ~1 m).
       Non interattivi: i clic e il tooltip restano agli archi. */
    useEffect(() => {
        const g = giuntiRef.current;
        const map = mapRef.current;
        if (!g || !map) return;
        g.clearLayers();
        let conGiunti = [];
        if (selezione) conGiunti = archi.filter((a) => selezione.has(a.id) || (modifica && originali?.has(a.id)));
        else if (map.getZoom() >= GIUNTI_ZOOM_MIN) {
            const area = map.getBounds().pad(0.2);
            conGiunti = archi.filter((a) => a.tratta && a.linea.some(([lng, lat]) => area.contains([lat, lng])));
        }
        const visti = new Set();
        for (const a of conGiunti) {
            for (const [lng, lat] of [a.linea[0], a.linea[a.linea.length - 1]]) {
                const k = `${lng.toFixed(5)}:${lat.toFixed(5)}`;
                if (visti.has(k)) continue;
                visti.add(k);
                L.circleMarker([lat, lng], {
                    renderer: rendererRef.current,
                    radius: selezione ? 4 : 3,
                    color: selezione ? COLORI.scelto : coloreDi(a.tratta),
                    weight: selezione ? 2 : 1.5,
                    fillColor: "#fff",
                    fillOpacity: 1,
                    interactive: false,
                }).addTo(g);
            }
        }
        markersRef.current?.eachLayer((m) => m.bringToFront());   // inizio e fine sopra ai giunti
    }, [archi, selezione, originali, modifica, coloreDi, vista]);

    /* Arco evidenziato (dal codice nel pannello o dal mouse sulla mappa): un alone scuro
       sotto l'arco, che poi torna in primo piano; pallini di inizio e fine sopra a tutto.
       Non interattivo, cosi' non ruba clic e passaggi del mouse agli archi. */
    useEffect(() => {
        const g = aloneRef.current;
        if (!g) return;
        g.clearLayers();
        const pl = evidenziato != null ? layersRef.current.get(evidenziato) : null;
        if (!pl) return;
        L.polyline(pl.getLatLngs(), { renderer: rendererRef.current, color: "#0F172A", weight: 14, opacity: 0.35, interactive: false }).addTo(g);
        pl.bringToFront();
        giuntiRef.current?.eachLayer((m) => m.bringToFront());
        markersRef.current?.eachLayer((m) => m.bringToFront());
    }, [evidenziato, archi]);

    // Inizio e fine della tratta scelta.
    useEffect(() => {
        const g = markersRef.current;
        if (!g) return;
        g.clearLayers();
        const punto = (p, colore, testo) => {
            if (!p) return;
            L.circleMarker([p[1], p[0]], { radius: 8, color: "#fff", weight: 2, fillColor: colore, fillOpacity: 1, interactive: false })
                .bindTooltip(testo, { permanent: true, direction: "top", offset: [0, -8], className: "gp-geo-etichetta" })
                .addTo(g);
        };
        punto(inizio, COLORI.ok, "Inizio");
        punto(fine, COLORI.libero, "Fine");
    }, [inizio, fine]);

    // Inquadra la tratta scelta (solo quando cambia tratta, non a ogni clic in modifica).
    useEffect(() => {
        const map = mapRef.current;
        if (!map || !fitKey || !selezione || !selezione.size) return;
        const ls = [...selezione].map((id) => layersRef.current.get(id)).filter(Boolean);
        if (ls.length) map.fitBounds(L.featureGroup(ls).getBounds(), { padding: [60, 60], maxZoom: 18 });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fitKey]);

    /* Inquadra gli archi chiesti dalla pagina (un arco senza tratta scelto nell'elenco).
       Ogni richiesta e' un array nuovo: anche ricliccare lo stesso arco lo reinquadra. */
    useEffect(() => {
        const map = mapRef.current;
        if (!map || !inquadra?.length) return;
        const ls = inquadra.map((id) => layersRef.current.get(id)).filter(Boolean);
        if (ls.length) map.fitBounds(L.featureGroup(ls).getBounds(), { padding: [60, 60], maxZoom: 18 });
    }, [inquadra]);

    return <div ref={nodeRef} className="gp-geo-mappa" />;
};

export default MappaArchiQgis;
